import { msg } from "@lingui/core/macro";
import { createSignal } from "solid-js";
import { createRoute } from "@tanstack/solid-router";
import { appRoute } from "./route";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/ui/page-container";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChoiceDialog } from "@/features/todos/components/choice-dialog";
import { ListDialog } from "@/features/todos/components/list-dialog";
import { GroupDialog } from "@/features/todos/components/group-dialog";
import { ShareListDialog } from "@/features/todos/components/share-list-dialog";
import { InviteMemberDialog } from "@/features/organizations/components/invite-member-dialog";
import { ChangeRoleDialog } from "@/features/organizations/components/change-role-dialog";
import { ListOptionsDialog } from "@/features/todos/components/list-options-dialog";
import { GroupOptionsDialog } from "@/features/todos/components/group-options-dialog";
import { AddToGroupDialog } from "@/features/todos/components/add-to-group-dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import {
  AlertTriangle,
  Hammer,
  Layers,
  LayoutGrid,
  MessageSquare,
  MoreHorizontal,
  PlusCircle,
  Settings,
  Share2,
  Shield,
  UserPlus,
} from "lucide-solid";

const SIMULATED_DELAY = 1000;

function ModalSandboxPage() {
  const { t } = useI18n();
  const [choiceOpen, setChoiceOpen] = createSignal(false);
  const [listOpen, setListOpen] = createSignal(false);
  const [groupOpen, setGroupOpen] = createSignal(false);
  const [shareOpen, setShareOpen] = createSignal(false);
  const [inviteOpen, setInviteOpen] = createSignal(false);
  const [roleOpen, setRoleOpen] = createSignal(false);
  const [listOptionsOpen, setListOptionsOpen] = createSignal(false);
  const [groupOptionsOpen, setGroupOptionsOpen] = createSignal(false);
  const [addToGroupOpen, setAddToGroupOpen] = createSignal(false);

  return (
    <div class="flex flex-1 flex-col min-h-0 overflow-hidden bg-background">
      <PageContainer maxWidth="4xl" class="space-y-8" containerClass="min-h-0">
        {/* Header */}
        <div>
          <h1 class="text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Hammer class="h-8 w-8 text-primary" />
            {t(msg`Modal Sandbox`)}
          </h1>
          <p class="text-sm text-muted-foreground">
            {t(
              "Interactive testing environment for validating user experience and transitions of all application modals.",
            )}
          </p>
        </div>

        {/* Modals Testing Cards Grid */}
        <div class="grid gap-6 sm:grid-cols-2">
          {/* Choice Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <LayoutGrid class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Choice Dialog`)}</CardTitle>
                <p class="text-xs text-muted-foreground">
                  {t(msg`Floating menu to create lists or groups`)}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <Button onClick={() => setChoiceOpen(true)} class="w-full">
                {t(msg`Open Choice Dialog`)}
              </Button>
            </CardContent>
          </Card>

          {/* List Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                <Layers class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`List Dialog`)}</CardTitle>
                <p class="text-xs text-muted-foreground">
                  {t(msg`Full form for creating/editing lists`)}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => setListOpen(true)}
                class="w-full bg-emerald-600 hover:bg-emerald-500 text-white border-none"
              >
                {t(msg`Open List Dialog`)}
              </Button>
            </CardContent>
          </Card>

          {/* Group Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500">
                <MessageSquare class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Group Dialog`)}</CardTitle>
                <p class="text-xs text-muted-foreground">
                  {t(msg`Form to organize list collections`)}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => setGroupOpen(true)}
                class="w-full bg-amber-600 hover:bg-amber-500 text-white border-none"
              >
                {t(msg`Open Group Dialog`)}
              </Button>
            </CardContent>
          </Card>

          {/* Share List Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                <Share2 class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Share List Dialog`)}</CardTitle>
                <p class="text-xs text-muted-foreground">{t(msg`List collaboration manager`)}</p>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => setShareOpen(true)}
                class="w-full bg-blue-600 hover:bg-blue-500 text-white border-none"
              >
                {t(msg`Open Share List Dialog`)}
              </Button>
            </CardContent>
          </Card>

          {/* Invite Member Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-500">
                <UserPlus class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Invite Member`)}</CardTitle>
                <p class="text-xs text-muted-foreground">{t(msg`Organization invitation form`)}</p>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => setInviteOpen(true)}
                class="w-full bg-indigo-600 hover:bg-indigo-500"
              >
                {t(msg`Open Invite Dialog`)}
              </Button>
            </CardContent>
          </Card>

          {/* Change Role Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500">
                <Shield class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Change Role`)}</CardTitle>
                <p class="text-xs text-muted-foreground">
                  {t(msg`Permission selector for members`)}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => setRoleOpen(true)}
                class="w-full bg-amber-600 hover:bg-amber-500"
              >
                {t(msg`Open Role Dialog`)}
              </Button>
            </CardContent>
          </Card>

          {/* List Options Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-slate-500/10 flex items-center justify-center text-slate-500">
                <MoreHorizontal class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`List Options`)}</CardTitle>
                <p class="text-xs text-muted-foreground">{t(msg`Quick action menu for lists`)}</p>
              </div>
            </CardHeader>
            <CardContent>
              <Button onClick={() => setListOptionsOpen(true)} variant="outline" class="w-full">
                {t(msg`Open List Options`)}
              </Button>
            </CardContent>
          </Card>

          {/* Group Options Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-slate-500/10 flex items-center justify-center text-slate-500">
                <Settings class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Group Options`)}</CardTitle>
                <p class="text-xs text-muted-foreground">{t(msg`Actions for organizing groups`)}</p>
              </div>
            </CardHeader>
            <CardContent>
              <Button onClick={() => setGroupOptionsOpen(true)} variant="outline" class="w-full">
                {t(msg`Open Group Options`)}
              </Button>
            </CardContent>
          </Card>

          {/* Add to Group Dialog */}
          <Card class="hover:shadow-lg transition-shadow sm:col-span-2">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-violet-500/10 flex items-center justify-center text-violet-500">
                <PlusCircle class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Add to Group`)}</CardTitle>
                <p class="text-xs text-muted-foreground">
                  {t(msg`Organizer of existing lists in groups`)}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => setAddToGroupOpen(true)}
                class="w-full bg-violet-600 hover:bg-violet-500"
              >
                {t(msg`Open Add to Group Dialog`)}
              </Button>
            </CardContent>
          </Card>

          {/* Confirm Dialog */}
          <Card class="hover:shadow-lg transition-shadow col-span-1 sm:col-span-2">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-destructive/10 flex items-center justify-center text-destructive">
                <AlertTriangle class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Confirm Dialog`)}</CardTitle>
                <p class="text-xs text-muted-foreground">
                  {t(msg`Confirmation window for destructive actions`)}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <ConfirmDialog
                title={t(msg`Do you want to save destructive changes?`)}
                description={t(
                  "This is an interactive demonstration of ConfirmDialog in the Modal Sandbox testing environment.",
                )}
                confirmLabel={t(msg`Confirm action`)}
                variant="destructive"
                onConfirm={() => {
                  showToast(t(msg`Action confirmed in Sandbox`), "success");
                }}
                renderTrigger={(open) => (
                  <Button onClick={open} variant="destructive" class="w-full">
                    {t(msg`Open Confirm Dialog`)}
                  </Button>
                )}
              />
            </CardContent>
          </Card>
        </div>

        {/* Overlay instances */}
        <ChoiceDialog
          open={choiceOpen()}
          onOpenChange={setChoiceOpen}
          onCreateList={() => {
            showToast(t(msg`New List selected`), "success");
            setListOpen(true);
          }}
          onCreateGroup={() => {
            showToast(t(msg`New Group selected`), "success");
            setGroupOpen(true);
          }}
        />

        <ListDialog open={listOpen()} onOpenChange={setListOpen} />

        <GroupDialog open={groupOpen()} onOpenChange={setGroupOpen} />

        <ShareListDialog
          open={shareOpen()}
          onOpenChange={setShareOpen}
          listId="demo-list-id"
          // eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n)
          listName="Demo List"
        />

        <InviteMemberDialog
          open={inviteOpen()}
          onOpenChange={setInviteOpen}
          // oxlint-disable-next-line typescript-eslint/promise-function-async
          onInvite={(email) => {
            console.log("Inviting:", email);
            return new Promise((resolve) => {
              setTimeout(resolve, SIMULATED_DELAY);
            });
          }}
        />

        <ChangeRoleDialog
          open={roleOpen()}
          onOpenChange={setRoleOpen}
          currentRole="member"
          memberName={t(msg`Juan Pérez`)}
          // oxlint-disable-next-line typescript-eslint/promise-function-async
          onRoleChange={(role) => {
            console.log("Role changed to:", role);
            return Promise.resolve();
          }}
        />

        <ListOptionsDialog
          open={listOptionsOpen()}
          onOpenChange={setListOptionsOpen}
          listName={t(msg`Shopping List`)}
          listId="list-1"
          groupId={null}
          groups={[
            { id: "g1", name: t(msg`Personal`) },
            { id: "g2", name: t(msg`Work`) },
          ]}
          onRename={() => {
            showToast(t(msg`Rename list`), "info");
          }}
          onDelete={() => {
            showToast(t(msg`Delete list`), "error");
          }}
          onMoveToGroup={(group) => {
            // eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): sandbox demo
            showToast(`Moved to group ${group ?? ""}`, "success");
          }}
        />

        <GroupOptionsDialog
          open={groupOptionsOpen()}
          onOpenChange={setGroupOptionsOpen}
          groupName={t(msg`Work`)}
          onRename={() => {
            showToast(t(msg`Rename group`), "info");
          }}
          onDelete={() => {
            showToast(t(msg`Delete group`), "error");
          }}
        />

        <AddToGroupDialog
          open={addToGroupOpen()}
          onOpenChange={setAddToGroupOpen}
          groupName={t(msg`Personal`)}
          ungroupedLists={[
            { id: "l1", name: t(msg`Books to read`), icon: "📚" },
            { id: "l2", name: t(msg`Movies`), icon: "🎬" },
          ]}
          onAddExistingList={(id) => {
            showToast(t(msg`List ${id} added`), "success");
          }}
          onAddNewList={() => {
            showToast(t(msg`Create new list`), "info");
          }}
        />
      </PageContainer>
    </div>
  );
}

export const sandboxModalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/modal",
  component: ModalSandboxPage,
});
